import React, { forwardRef, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import {
  IconButton,
  Icon,
  ButtonGroup,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  useDisclosure,
} from "@chakra-ui/react";
import { BiEdit } from "react-icons/bi";
import { validUrlWithProtocol } from "../../../_utils/validator";
import { translate as trans } from "../Trans/Trans";

const EditButton = forwardRef((props, ref) => (
  <Tooltip
    label={trans("post_link_preview.link.click_to_change")}
    hasArrow
    placement="top"
    shouldWrapChildren
  >
    <IconButton
      {...props}
      ref={ref}
      variant="unstyled"
      size="xs"
      icon={<Icon fontSize="md" as={BiEdit} />}
    />
  </Tooltip>
));

const PostLinkPreviewEditLinkPopover = ({ value: linkValue, onSave }) => {
  const firstFieldRef = useRef(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      url: linkValue || "",
    },
    shouldUnregister: true,
  });
  const { onOpen, onClose, isOpen } = useDisclosure();

  const handleUrlValidate = useCallback(
    (value) => {
      const validated = !value || validUrlWithProtocol(value);

      return validated
        ? true
        : trans("post_link_preview.validation.url_incorrect");
    },
    [validUrlWithProtocol]
  );

  const onSubmit = useCallback(
    ({ url }) => {
      onClose();
      onSave?.(url);
    },
    [onClose, onSave]
  );

  return (
    <Popover
      isOpen={isOpen}
      initialFocusRef={firstFieldRef}
      onOpen={onOpen}
      onClose={onClose}
      placement="top"
    >
      <PopoverTrigger>
        <EditButton />
      </PopoverTrigger>
      <PopoverContent padding="2">
        <PopoverArrow />
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl isInvalid={errors?.url} marginBottom="2">
            <FormLabel fontSize="sm">
              {trans("post_link_preview.link.url")}
            </FormLabel>
            <Input
              size="sm"
              placeholder={trans("post_link_preview.link.url_placeholder")}
              {...register("url", {
                required: {
                  value: true,
                  message: trans("post_link_preview.validation.url_required"),
                },
                validate: {
                  url: handleUrlValidate,
                },
              })}
            />
            {errors?.url?.message ? (
              <FormErrorMessage fontSize="xs">
                {errors?.url?.message}
              </FormErrorMessage>
            ) : null}
          </FormControl>
          <ButtonGroup display="flex" justifyContent="flex-end" size="sm">
            <Button onClick={onClose} variant="secondary">
              {trans("generic.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting} variant="solid">
              {trans("generic.save")}
            </Button>
          </ButtonGroup>
        </form>
      </PopoverContent>
    </Popover>
  );
};

PostLinkPreviewEditLinkPopover.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func,
};

PostLinkPreviewEditLinkPopover.defaultProps = {
  value: undefined,
  onSave: undefined,
};

export default PostLinkPreviewEditLinkPopover;
